import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  GraduationCap,
  Users,
  Disc3,
  Palette,
  Calendar,
  Hotel,
  CreditCard,
  UserCircle,
  Heart,
  BarChart3,
  Gamepad2,
  Dices,
  Mail,
  FileText,
  MessageSquare,
  Bell,
  Image,
  ShoppingBag,
  Download,
  FileSpreadsheet,
  Key,
  Receipt,
  FileCode,
  Activity,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Home,
  PanelLeftClose,
  PanelLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

// Navigation structure with groups
const navigationGroups = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: null, // Direct destination
    tabId: 'overview'
  },
  {
    id: 'directory',
    label: 'Directory',
    icon: Building2,
    items: [
      { id: 'clubs', label: 'Clubs', icon: Building2, tabId: 'clubs' },
      { id: 'classes', label: 'Classes', icon: GraduationCap, tabId: 'classes' },
      { id: 'instructors', label: 'Instructors', icon: Users, tabId: 'instructors' },
      { id: 'djs', label: 'DJs', icon: Disc3, tabId: 'djs' },
      { id: 'artists', label: 'Artists', icon: Palette, tabId: 'artists' }
    ]
  },
  {
    id: 'events',
    label: 'Events',
    icon: Calendar,
    items: [
      { id: 'events', label: 'Events', icon: Calendar, tabId: 'events' }
    ]
  },
  {
    id: 'weekenders',
    label: 'Weekenders',
    icon: Hotel,
    items: [
      { id: 'weekends', label: 'Weekends', icon: Hotel, tabId: 'weekends' },
      { id: 'payment-plans', label: 'Payment Plans', icon: CreditCard, tabId: 'payment-plans' }
    ]
  },
  {
    id: 'people',
    label: 'People',
    icon: UserCircle,
    items: [
      { id: 'users', label: 'Users', icon: Users, tabId: 'users' },
      { id: 'members', label: 'Members', icon: Heart, tabId: 'members' }
    ]
  },
  {
    id: 'activities',
    label: 'Activities',
    icon: Gamepad2,
    items: [
      { id: 'dance-charts', label: 'Dance Charts', icon: BarChart3, tabId: 'dance-charts' },
      { id: 'bingo-dances', label: 'Bingo Dances', icon: Gamepad2, tabId: 'bingo-dances' },
      { id: 'bingo-games', label: 'Bingo Games', icon: Dices, tabId: 'bingo-games' }
    ]
  },
  {
    id: 'content',
    label: 'Content & Marketing',
    icon: Mail,
    items: [
      { id: 'newsletter', label: 'Newsletter', icon: Mail, tabId: 'newsletter' },
      { id: 'blog', label: 'Blog', icon: FileText, tabId: 'blog' },
      { id: 'reviews', label: 'Reviews', icon: MessageSquare, tabId: 'reviews' },
      { id: 'notifications', label: 'Notifications', icon: Bell, tabId: 'notifications' },
      { id: 'images', label: 'Images', icon: Image, tabId: 'images' }
    ]
  },
  {
    id: 'shop',
    label: 'Shop',
    icon: ShoppingBag,
    items: null, // Direct destination
    tabId: 'shop'
  },
  {
    id: 'imports',
    label: 'Imports & Data',
    icon: Download,
    items: [
      { id: 'scraper', label: 'Event Importer', icon: Download, tabId: 'scraper' },
      { id: 'scraped-classes', label: 'Imported Classes', icon: FileSpreadsheet, tabId: 'scraped-classes' }
    ]
  },
  {
    id: 'system',
    label: 'System',
    icon: Key,
    items: [
      { id: 'dj-access', label: 'DJ Access', icon: Key, tabId: 'dj-access' },
      { id: 'subscriptions', label: 'Subscriptions', icon: Receipt, tabId: 'subscriptions' },
      { id: 'email-templates', label: 'Email Templates', icon: FileCode, tabId: 'email-templates' },
      { id: 'email-analytics', label: 'Email Analytics', icon: Activity, tabId: 'email-analytics' },
      { id: 'audit', label: 'Audit Log', icon: ClipboardList, tabId: 'audit' }
    ]
  }
];

// Helper to find current item and group from tabId
const findNavItem = (tabId) => {
  for (const group of navigationGroups) {
    if (group.items === null && group.tabId === tabId) {
      return { group, item: null };
    }
    if (group.items) {
      const item = group.items.find(i => i.tabId === tabId);
      if (item) {
        return { group, item };
      }
    }
  }
  return { group: navigationGroups[0], item: null };
};

// Sidebar Navigation Item
const NavItem = ({ item, isActive, onClick, collapsed }) => {
  const Icon = item.icon;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-900",
        isActive
          ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
          : "text-slate-300 hover:bg-slate-800 hover:text-white"
      )}
      aria-current={isActive ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
    >
      <Icon className={cn("flex-shrink-0", collapsed ? "w-5 h-5" : "w-4 h-4")} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </button>
  );
};

// Sidebar Group
const NavGroup = ({ group, activeTabId, onSelectTab, collapsed, expandedGroups, toggleGroup }) => {
  const Icon = group.icon;
  const isExpanded = expandedGroups.includes(group.id);
  const hasActiveItem = group.items?.some(item => item.tabId === activeTabId) || 
                        (group.items === null && group.tabId === activeTabId);
  
  // Direct destination (no sub-items)
  if (group.items === null) {
    const isActive = group.tabId === activeTabId;
    return (
      <button
        onClick={() => onSelectTab(group.tabId)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-900",
          isActive
            ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
            : "text-slate-300 hover:bg-slate-800 hover:text-white"
        )}
        aria-current={isActive ? 'page' : undefined}
        title={collapsed ? group.label : undefined}
      >
        <Icon className={cn("flex-shrink-0", collapsed ? "w-5 h-5" : "w-4 h-4")} />
        {!collapsed && <span className="truncate">{group.label}</span>}
      </button>
    );
  }
  
  return (
    <div className="space-y-1">
      <button
        onClick={() => toggleGroup(group.id)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-900",
          hasActiveItem
            ? "text-violet-300 bg-slate-800/50"
            : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-300"
        )}
        aria-expanded={isExpanded}
        title={collapsed ? group.label : undefined}
      >
        <div className="flex items-center gap-3">
          <Icon className={cn("flex-shrink-0", collapsed ? "w-5 h-5" : "w-4 h-4")} />
          {!collapsed && <span className="truncate">{group.label}</span>}
        </div>
        {!collapsed && (
          isExpanded 
            ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> 
            : <ChevronRight className="w-4 h-4 flex-shrink-0" />
        )}
      </button>
      
      {!collapsed && isExpanded && (
        <div className="ml-4 pl-3 border-l border-slate-700 space-y-1">
          {group.items.map(item => (
            <NavItem
              key={item.id}
              item={item}
              isActive={item.tabId === activeTabId}
              onClick={() => onSelectTab(item.tabId)}
              collapsed={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main AdminLayout Component
export function AdminLayout({ 
  children, 
  activeTab, 
  onTabChange, 
  user,
  onLogout 
}) {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('admin-sidebar-collapsed');
    return saved === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Initialize with the group containing the active tab expanded
    const { group } = findNavItem(activeTab);
    return group.items ? [group.id] : [];
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('admin-sidebar-collapsed', collapsed.toString());
  }, [collapsed]);

  // Expand group when active tab changes
  useEffect(() => {
    const { group } = findNavItem(activeTab);
    if (group.items && !expandedGroups.includes(group.id)) {
      setExpandedGroups(prev => [...prev, group.id]);
    }
  }, [activeTab]);

  // Close mobile menu on tab change
  useEffect(() => {
    setMobileOpen(false);
  }, [activeTab]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSelectTab = (tabId) => {
    onTabChange(tabId);
    setMobileOpen(false);
  };

  // Get breadcrumb info
  const { group, item } = findNavItem(activeTab);
  const breadcrumbs = item 
    ? [{ label: group.label }, { label: item.label }]
    : [{ label: group.label }];

  // Sidebar content (shared between desktop and mobile)
  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo/Brand */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-5 border-b border-slate-700/50",
        collapsed && !isMobile && "justify-center px-2"
      )}>
        <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">LC</span>
        </div>
        {(!collapsed || isMobile) && (
          <div className="min-w-0">
            <h1 className="font-bold text-white truncate">Line Dance Central</h1>
            <p className="text-xs text-slate-400">Administration</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav 
        className="flex-1 overflow-y-auto py-4 px-3 space-y-2"
        aria-label="Admin navigation"
      >
        {navigationGroups.map(group => (
          <NavGroup
            key={group.id}
            group={group}
            activeTabId={activeTab}
            onSelectTab={handleSelectTab}
            collapsed={collapsed && !isMobile}
            expandedGroups={expandedGroups}
            toggleGroup={toggleGroup}
          />
        ))}
      </nav>

      {/* User section */}
      <div className={cn(
        "border-t border-slate-700/50 p-4",
        collapsed && !isMobile && "px-2"
      )}>
        {(!collapsed || isMobile) ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCircle className="w-5 h-5 text-slate-300" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to="/"
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </Link>
              <button
                onClick={onLogout}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
              <UserCircle className="w-5 h-5 text-slate-300" />
            </div>
            <Link
              to="/"
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Home"
            >
              <Home className="w-4 h-4" />
            </Link>
            <button
              onClick={onLogout}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 transform transition-transform duration-300 ease-in-out lg:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
        aria-label="Mobile navigation"
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
        <SidebarContent isMobile={true} />
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 bg-slate-900 border-r border-slate-800 transition-all duration-300 hidden lg:block",
          collapsed ? "w-16" : "w-64"
        )}
        aria-label="Desktop navigation"
      >
        <SidebarContent />
        
        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors shadow-lg"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeft className="w-3 h-3" /> : <PanelLeftClose className="w-3 h-3" />}
        </button>
      </aside>

      {/* Main Content Area */}
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "lg:ml-16" : "lg:ml-64"
      )}>
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Breadcrumbs */}
            <nav className="hidden sm:flex items-center gap-2 text-sm" aria-label="Breadcrumb">
              <Link 
                to="/admin" 
                className="text-slate-500 hover:text-violet-600 transition-colors"
              >
                Admin
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                  <span className={cn(
                    index === breadcrumbs.length - 1 
                      ? "text-slate-900 font-medium" 
                      : "text-slate-500"
                  )}>
                    {crumb.label}
                  </span>
                </React.Fragment>
              ))}
            </nav>

            {/* Mobile: Current page title */}
            <h1 className="sm:hidden text-lg font-semibold text-slate-900">
              {item?.label || group.label}
            </h1>

            {/* User info (desktop) */}
            <div className="hidden md:flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Logged in as <span className="font-medium text-slate-900">{user?.email}</span>
              </span>
            </div>
            
            {/* Spacer for mobile */}
            <div className="w-10 lg:hidden" />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
